import { useState, useRef, useEffect } from 'react';
import { Gear, CyclicAction, BinaryAction } from '@components/operatorLobby/ControlStream';

const LeftButtonMapping = {
    FlipTriggerSwitch : 0,
    FlipTriggerDown : 1,
    TriggerHalf : 2,
    TriggerFull : 3,
    ThumbPress : 4,
    RedRoundButton : 5,
    PyramidPress : 6,
    PyramidUp : 7,
    PyramidRight : 8,
    PyramidDown : 9,
    PyramidLeft : 10,
    BlackRoundButton : 11,
    RampPress : 12,
    RampUp : 13,
    RampRight : 14,
    RampDown : 15,
    RampLeft : 16,
    LeftSideRampPress : 17,
    LeftSideRampUp : 18,
    LeftSideRampDown : 19,
    WheelPressFull : 20,
    WheelPressHalf : 21,
    WheelUp : 22,
    WheelDown : 23,
    RightSideRampPress : 24,
    RightSideRampUp : 25,
    RightSideRampRight : 26,
    RightSideRampDown : 27,
    RightSideRampLeft : 28,
    BackButton : 29,
    BrakeLever : 30,
}

// Up/Down is y
// Left/Right is x
const AxesMapping = {
    xAxis : 0, // X
    yAxis : 1, // Y 
    zAxis : 2, // Z
    thumbXAxis : 3, // rX
    thumbYAxis : 4, // rY
    // : 5, // ??
    brakeAxis : 6  // Slider
};

export default function useJoystickLeft() {

    const gamepadIndexRef = useRef(null);
    const [isConnected, setConnected] = useState(false);
    const [enable, setEnable] = useState(true);
    const [controlUpdate, setControlUpdate] = useState(null);
    const [controlSend, setControlSend] = useState(null);
    const [vehicleStateRequest, setVehicleStateRequest] = useState(null);
    const lastAxisUpdateTime = useRef(Date.now());
    const [goalPosition, setGoalPosition] = useState({enabled: false, position: 0});

    let angularSpeedLimit = 0.0;
    let angularSpeedLimitIncrement = 1.2;
    let maxAngularSpeedLimit = 6.0;

    useEffect(() => {
        function onGamepadConnected(event) {
            const gamepad = event.gamepad;
            if (/L-VPC Stick MT-50CM2/i.test(gamepad.id)) {
                gamepadIndexRef.current = event.gamepad.index;
                setConnected(true);
                console.log(
                    'Gamepad connected at index %d: %s. %d buttons, %d axes.',
                    event.gamepad.index,
                    event.gamepad.id,
                    event.gamepad.buttons.length,
                    event.gamepad.axes.length
                )
            } 
        }

        function onGamepadDisconnected(event) {
            const gamepad = event.gamepad;
            if (/L-VPC Stick MT-50CM2/i.test(gamepad.id)) {
                gamepadIndexRef.current = null;
                setConnected(false);
                console.warn('%s disconnected.', event.gamepad.id);
            }
        }

        window.addEventListener('gamepaddisconnected', onGamepadDisconnected);
        window.addEventListener('gamepadconnected', onGamepadConnected);

        return () => {
            window.removeEventListener('gamepadconnected', onGamepadConnected);
            window.removeEventListener('gamepaddisconnected', onGamepadConnected);
        };
    }, []);

    useEffect(() => {
        if(enable && isConnected && controlUpdate && controlSend && vehicleStateRequest) {
            const gamepad = navigator.getGamepads()[gamepadIndexRef.current];

            let lastGamepadState = {
                buttons: gamepad.buttons.map((button) => button.pressed),
                axes: gamepad.axes.map((value) => value), // Necessary to avoid sharing array
            }

            function onButtonChanged(index, pressed) {
                if(index === LeftButtonMapping.Horn) {
                    //controlUpdate(control => ({ ...control, horn: pressed ? BinaryAction.Set : BinaryAction.Clear }));
                }
            }
        
            function onButtonDown(index) {
                switch(index) {
                    case LeftButtonMapping.RedRoundButton:
                        vehicleStateRequest('emergency');  
                        break;
                    case LeftButtonMapping.PyramidUp:
                        vehicleStateRequest('maintenance');
                        break;
                    case LeftButtonMapping.PyramidLeft:
                        vehicleStateRequest('driving');
                        break;
                    case LeftButtonMapping.PyramidRight:
                        vehicleStateRequest('parking');   
                        break;
                    case LeftButtonMapping.PyramidDown:
                        vehicleStateRequest('autonomous');
                        break;
                    case LeftButtonMapping.WheelUp:
                        controlUpdate(control => ({ ...control, increaseLinearSpeed: BinaryAction.Flip }));
                        controlUpdate(control => ({ ...control, increaseAngularSpeed: BinaryAction.Flip }));
                        increaseAngularSpeedLimit();
                        break;
                    case LeftButtonMapping.WheelDown:
                        controlUpdate(control => ({ ...control, decreaseLinearSpeed: BinaryAction.Flip }));
                        controlUpdate(control => ({ ...control, decreaseAngularSpeed: BinaryAction.Flip }));
                        decreaseAngularSpeedLimit();
                        break;
                    case LeftButtonMapping.BlackRoundButton:
                        controlUpdate(control => ({ ...control, tool: true }));
                        break;
                    case LeftButtonMapping.BrakeLever:
                        break;    
                    case LeftButtonMapping.TriggerHalf:
                        controlUpdate(control => ({ ...control, engine: BinaryAction.Flip })); // engine
                        break;
                    case LeftButtonMapping.FlipTriggerDown:
                        controlUpdate(control => ({ ...control, ignition: BinaryAction.Set })); //ignition
                        break;
                    case LeftButtonMapping.RampPress:
                        controlUpdate(control => ({ ...control, electricAux: BinaryAction.Flip })); // floating tool left-right
                        break;
                    default:
                        break;
                }
            }
        
            function onButtonUp(index) {
                switch(index) {
                    case LeftButtonMapping.BlackRoundButton:
                        controlUpdate(control => ({ ...control, tool: false }));
                        break;
                    default:
                        break;
                }
            }

            function increaseAngularSpeedLimit() {
                angularSpeedLimit = angularSpeedLimit + angularSpeedLimitIncrement;
                if(angularSpeedLimit > maxAngularSpeedLimit){
                    angularSpeedLimit = maxAngularSpeedLimit;
                }
                controlUpdate(control => ({ ...control, angularSpeedLimit: Math.abs(angularSpeedLimit) })); 
            }

            function decreaseAngularSpeedLimit() {
                angularSpeedLimit = angularSpeedLimit - angularSpeedLimitIncrement;
                if(angularSpeedLimit < 0.0){
                    angularSpeedLimit = 0.0;
                }
                controlUpdate(control => ({ ...control, angularSpeedLimit: Math.abs(angularSpeedLimit) })); 
            }

        
            function mapRange(valueIn, startIn, endIn, startOut, endOut) {
                const scale = (endOut - startOut) / (endIn - startIn);
                return startOut + (valueIn - startIn) * scale;
            }
        
            function onAxisChanged(index, _oldValue, value) {
                switch(index) {
                    case AxesMapping.xAxis:  
                        controlUpdate(control => ({ ...control, angularSpeed: value })); // Left is negative
                        break;
                    case AxesMapping.yAxis:
                        controlUpdate(control => ({ ...control, linearSpeed: -value })); // Up is negative
                        break;
                    case AxesMapping.zAxis:
                        // Left is negative
                        break;
                    case AxesMapping.thumbXAxis:
                        // Left is negative
                        break;
                    case AxesMapping.thumbYAxis:
                        // Up is negative
                        break;
                    case AxesMapping.brakeAxis:
                        break;
                    default:
                        break;
                }
            }

            let animationRequestId = window.requestAnimationFrame(poll);

            function poll() {
                const gamepad = navigator.getGamepads()[gamepadIndexRef.current];
                if(gamepad) {
                    let hasControlBeenUpdated = false;

                    for(let i = 0; i < gamepad.buttons.length; i++) {
                        const oldValue = lastGamepadState.buttons[i];
                        const newValue = gamepad.buttons[i].pressed
                        lastGamepadState.buttons[i] = newValue;
                        if(oldValue !== newValue) {
                            if(newValue) {
                                onButtonDown(i);
                            } else {
                                onButtonUp(i);
                            }
                            onButtonChanged(i, newValue);
                            // console.log(`Button ${i}`);  
                            hasControlBeenUpdated = true;
                        }
                    }

                    const now = Date.now(); // Downsample the rate
                    const deltaTime = now - lastAxisUpdateTime.current;
                    const thresholdTime = 100; // 10Hz
                    
                    for(let i = 0; i < gamepad.axes.length; i++) {
                        const oldValue = lastGamepadState.axes[i];
                        const newValue = gamepad.axes[i];
                        if(oldValue !== newValue) {
                            if(deltaTime > thresholdTime || oldValue === 0.0 || newValue === 0.0 || Math.abs(oldValue - newValue) > 0.1) { // Ignore timer when there is a change from or to zero or if change is more than 10%
                                lastGamepadState.axes[i] = newValue;
                                onAxisChanged(i, oldValue, newValue);
                                // console.log(`Axis ${i}`);  
                                hasControlBeenUpdated = true;
                            }
                        }
                    }

                    if(deltaTime > thresholdTime) {
                        lastAxisUpdateTime.current = now;
                    }

                    if(hasControlBeenUpdated) {
                        controlSend();
                    }

                    animationRequestId = window.requestAnimationFrame(poll);
                }
            }
            
            return () => {
                window.cancelAnimationFrame(animationRequestId);
            };
        }
    }, [isConnected, controlUpdate, controlSend, vehicleStateRequest, enable]);

    useEffect(() => {
        if (window.electron) {
            window.electron.steeringWheelSetGoalPosition(goalPosition)
        }
    }, [goalPosition])

    return {
        isConnected,
        enable,
        setEnable,
        setControlUpdate,
        setControlSend,
        setVehicleStateRequest,
        setGoalPosition,
    };
}
